<template>
  <div ref="orderCardRef" role="order-card">
    <div role="order-card__header" class="flex justify-between max-lg:flex-col">
      <div class="flex items-center max-md:justify-between pr-3">
        <div
          class="flex max-w-64 items-center font-light uppercase text-gray-about"
        >
          {{ $t("orderDate") }}:
          {{ format(new Date(order.customer_date), "MM/dd/yyyy") }}
        </div>

        <div
          v-if="order.status_title"
          class="ui-text-sm ml-2 flex h-7.5 items-center self-center rounded-full px-6"
          :class="badgeContent.color"
        >
          {{ order.status_title }}
        </div>

        <template v-if="showReturnButtons">
          <UiButton
            color="primary"
            class="ml-3 min-h-8 text-xs uppercase max-md:hidden whitespace-nowrap"
            size="sm"
            data-e2e-cta="return-request"
            @click="
              isReturn = true;
              isExpanded = true;
              isService = false;
            "
          >
            {{ $t("requestReturn") }}
          </UiButton>

          <UiButton
            v-if="serviceProducts.length > 0"
            color="secondary"
            class="ml-3 min-h-8 text-xs uppercase max-md:hidden whitespace-nowrap"
            size="sm"
            data-e2e-cta="service-request"
            @click="
              isReturn = true;
              isExpanded = true;
              isService = true;
            "
          >
            {{ $t("requestService") }}
          </UiButton>
        </template>
      </div>

      <UiButton
        v-if="!isReturn"
        variant="text"
        class="shrink-0 px-0 capitalize max-lg:hidden"
        data-e2e-cta="order-status"
        @click.prevent="toggleDetails"
      >
        {{ isExpanded ? $t("lessDetails") : $t("viewDetails") }}
        <UiIcon
          :name="isExpanded ? 'chevron_up' : 'chevron_down'"
          class="ml-2 size-6 shrink-0"
        />
      </UiButton>
    </div>

    <div
      role="order-card__body"
      class="mt-4 border border-gray-strokes px-2.5 py-4 lg:p-10"
    >
      <form class="flex flex-col" @submit.prevent="sendForm">
        <ul class="grid gap-y-7">
          <template v-for="(product, index) in orderProductsData" :key="index">
            <li
              v-if="
                !isReturn || (isReturn && product.quantityAvailableToReturn && !product.is_service_product)
              "
              class="flex flex-col"
              :class="{
                'border-t border-gray-strokes pt-7 first:border-t-0 first:pt-0':
                  isExpanded && isReturn,
              }"
            >
              <ProfileOrderProductCard
                :product="product"
                :is-expanded="isExpanded"
                :is-return="isReturn"
              />

              <template v-if="isReturn && isExpanded">
                <div class="mt-4 max-md:space-y-4 md:flex md:gap-x-5">
                  <div class="md:w-[85px]">
                    <UiNumberInput
                      v-model="returnForm.products[index].quantity"
                      :name="`products[${index}].quantity`"
                      input-classes="w-[60px] text-center"
                      :min="0"
                      :max="product.quantityAvailableToReturn"
                      :step="1"
                      :precision="0"
                      :disabled="!product.can_be_returned_by_timeframe"
                      :hide-details="!product.can_be_returned_by_timeframe"
                      :label="$t('qty') + '*'"
                      class="[&_span:last-child]:md:whitespace-nowrap"
                      data-e2e-item="refund-qty"
                    />
                  </div>
                  <UiSelect
                    :model-value="returnForm.products[index].type"
                    :items="returnTypes"
                    class="md:min-w-[190px] lg:min-w-[234px]"
                    title-key="label"
                    value-key="value"
                    :placeholder="$t('selectType')"
                    :name="`products[${index}].type`"
                    :disabled="!returnForm.products[index].quantity"
                    :label="$t('type') + '*'"
                    data-e2e-dropdown="refund-type"
                    data-e2e-item="refund-type"
                    @update:modelValue="handleTypeChange($event, index)"
                  />
                  <!--
                  <UiSelect
                    v-model="returnForm.products[index].reason_id"
                    :items="
                      getAllowedReturnReasonsByReturnType(
                        returnForm.products[index].type?.value,
                      )
                    "
                    class="md:min-w-[190px] lg:min-w-[234px]"
                    title-key="label"
                    value-key="value"
                    :placeholder="$t('selectReason')"
                    :name="`products[${index}].reason_id`"
                    :disabled="
                      !returnForm.products[index].quantity ||
                      !returnForm.products[index].type
                    "
                    :label="$t('reasonForReturn') + '*'"
                    data-e2e-dropdown="refund-reason"
                  />
                  -->
                </div>

                <div
                  v-if="
                    returnForm.products[index].quantity &&
                    returnForm.products[index].type
                  "
                  class="mt-5 space-y-5 border border-gray-strokes p-5"
                >
                  <div class="font-medium capitalize">
                    {{ $t("select") }}
                    {{ returnForm.products[index].type?.label }}
                    {{
                      returnForm?.products?.[index]?.type?.value ===
                      returnTypesEnum?.service
                        ? $t("type")
                        : $t("reason")
                    }}
                  </div>

                  <div class="space-y-2.5">
                    <UiRadioCard
                      v-for="option in getAllowedReturnReasonsByReturnType(
                        returnForm.products[index].type?.value,
                      )"
                      :key="option.value"
                      :model-value="getReasonIdValue(index)"
                      :name="`products[${index}].reason_id`"
                      :value="option.value"
                      :title="option.label"
                      :description="option.description || ''"
                      :price="null"
                      @update:model-value="setReasonIdValue(index, $event)"
                    />
                    <SharedFieldHint
                      :error="Boolean(getReasonIdError(index))"
                      :message="getReasonIdError(index)"
                    />
                  </div>

                  <div
                    v-if="
                      returnForm?.products?.[index]?.type?.value ===
                      returnTypesEnum?.service
                    "
                    class="text-sm font-light italic"
                  >
                    *{{
                      $t("allPricesFinalizedAfterOurJewelersReviewYourPiece")
                    }}
                  </div>

                  <UiTextarea
                    v-model="returnForm.products[index].comment"
                    class="flex-1"
                    :disabled="!returnForm.products[index].quantity"
                    :name="`products[${index}].comment`"
                    :placeholder="$t('writeAFewWords')"
                    :label="$t('additionalCommentsOptional')"
                    data-e2e-placeholder="additional-comment"
                  />
                </div>
              </template>
            </li>
          </template>
        </ul>

        <SharedCollapseTransition>
          <div v-if="isExpanded || isReturn">
            <div
              v-if="!isReturn"
              class="mt-7 flex flex-col gap-x-14.5 border-t border-gray-strokes pt-7.5 lg:flex-row"
            >
              <div class="flex-1 font-light">
                <h3 class="ui-text-l mb-1 capitalize text-gray-about">
                  {{
                    isCanceledStatusExist
                      ? $t("orderCancelled")
                      : $t("processingOrder")
                  }}
                </h3>

                <p class="mb-5 text-sm capitalize text-gray-about">
                  {{
                    isCanceledStatusExist
                      ? $t("cancelOrderText")
                      : `${$t("estimatedShipping")}: ${estimateShipping}`
                  }}
                </p>

                <SharedOrderStatus
                  :status="order.status"
                  :items="order.statuses"
                  :order="order"
                />
              </div>

              <SharedOrderPaymentInfo
                :order-data="order"
                class="max-lg:mt-7 max-lg:border-t max-lg:border-gray-strokes max-lg:pt-7"
              />
            </div>

            <div class="mt-7 flex border-t border-gray-strokes pt-7">
              <div
                class="flex w-full flex-col items-center gap-7 font-light text-gray-about lg:flex-row lg:justify-between"
              >
                <div class="flex items-center">
                  <UiIcon name="phone" class="mr-2.5 size-6 text-gray-about" />
                  <div>
                    {{ $t("haveQuestion") }}
                    <nuxt-link
                      :to="localePath('/contact-us')"
                      class="whitespace-nowrap underline"
                    >
                      {{ $t("contactUs") }}
                    </nuxt-link>
                  </div>
                </div>

                <div class="lg:text-right">
                  {{ $t("seeOur") }}
                  <nuxt-link
                    :to="
                      localePath(
                        '/customer-service?acl=85#thirty-day-free-returns',
                      )
                    "
                    class="whitespace-nowrap capitalize underline"
                    >{{ $t("returnsFAQ") }}</nuxt-link
                  >
                </div>
              </div>
            </div>
          </div>
        </SharedCollapseTransition>

        <UiButton
          v-if="!isReturn"
          size="sm"
          color="primary"
          variant="outlined"
          class="mt-7 flex w-full uppercase lg:hidden"
          @click.prevent="toggleDetails"
        >
          {{ isExpanded ? $t("lessDetails") : $t("viewDetails") }}
          <UiIcon
            :name="isExpanded ? 'chevron_up' : 'chevron_down'"
            class="ml-2 size-6 shrink-0"
          />
        </UiButton>
      </form>
      <div
        v-if="isReturn && isService && returnsInfoBlock"
        class="mt-7 border border-gray-40 bg-orange-10 p-5 text-sm font-light leading-5 text-black lg:p-10"
        v-html="returnsInfoBlock"
      />
    </div>
    <div
      v-if="isReturn"
      class="mt-5 flex justify-end gap-2.5 max-md:flex-col-reverse md:gap-5"
    >
      <UiButton
        color="primary"
        class="min-h-8 w-full text-xs uppercase md:w-[160px]"
        variant="outlined"
        size="sm"
        :disabled="isSubmitting"
        data-e2e-cta="refund-cancel"
        @click="isReturn = false"
      >
        {{ $t("cancel") }}
      </UiButton>
      <UiButton
        v-if="isService"
        color="secondary"
        class="min-h-8 w-full text-xs uppercase md:w-[160px]"
        type="submit"
        size="sm"
        :loading="isSubmitting"
        data-e2e-submit="request-return"
        @click="sendForm"
      >
        {{ $t("requestService") }}
      </UiButton>
      <UiButton
        v-else
        color="primary"
        class="min-h-8 w-full text-xs uppercase md:w-[160px]"
        type="submit"
        size="sm"
        :loading="isSubmitting"
        data-e2e-submit="request-return"
        @click="sendForm"
      >
        {{ $t("requestReturn") }}
      </UiButton>
    </div>
    <template v-if="showReturnButtons">
      <UiButton
        color="primary"
        class="mt-2 w-full text-xs uppercase md:hidden"
        size="sm"
        data-e2e-cta="return-request"
        @click="
          isReturn = true;
          isExpanded = true;
          isService = false;
        "
      >
        {{ $t("requestReturn") }}
      </UiButton>
      <UiButton
        v-if="serviceProducts.length > 0"
        color="secondary"
        class="mt-2 w-full text-xs uppercase md:hidden"
        size="sm"
        data-e2e-cta="service-request"
        @click="
          isReturn = true;
          isExpanded = true;
          isService = true;
        "
      >
        {{ $t("requestService") }}
      </UiButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { format } from "date-fns";
import { OrderData, OrderStatus, orderStatusesOptions } from "ecom-types";
import { UiSelectModelValue } from "~/types/components.types";

const props = defineProps<{
  order: OrderData;
  returnsInfoBlock?: string;
}>();
const emit = defineEmits(["updateOrderData"]);
const { openSystemDialog } = useSystemDialogs();
const { t } = useI18n();
const localePath = useLocalePathPolyfill();
const { estimateShipping } = useEstimateShipping(
  props.order.ship_date,
  "EEEE, LLLL dd, yyyy",
);
const isExpanded = ref(false);
const orderCardRef = ref<HTMLElement | null>(null);

const {
  isReturn,
  returnForm,
  returnTypesWithConditions,
  sendForm,
  orderProductsData,
  isSubmitting,
  order: useOrderData,
  getAllowedReturnReasonsByReturnType,
  setFieldValue,
  errors,
  serviceProducts,
} = useReturn(props.order, useForm, { successCallback, errorCallback });

const isService = ref(false);

// Reset form when switching between return and service modes
watch(isService, () => {
  if (returnForm.value.products) {
    returnForm.value.products.forEach((product: any) => {
      product.quantity = 0;
      product.type = null;
      product.reason_id = null;
      product.comment = '';
    });
  }
});

watch(
  () => props.order,
  () => {
    useOrderData.value = props.order;
  },
  {
    deep: true,
  },
);

watch(isReturn, () => {
  nextTick(() => {
    scrollToCard();
  });
});

const isCanceledStatusExist = computed(() => {
  return props.order?.statuses?.find((el) => isCanceledStatus(el));
});

const badgeContent = computed(() => {
  if (isCanceledStatusExist.value) {
    return {
      color: "bg-red-10 text-primary",
    };
  } else if (
    props.order.status === orderStatusesOptions.IN_TRANSIT ||
    props.order.status === orderStatusesOptions.DELIVERED
  ) {
    return {
      color: "bg-[#D9E3C8] text-primary",
    };
  } else {
    return {
      color: "bg-order-label text-primary",
    };
  }
});

const refundOrRepairAvailable = computed(() => {
  if (!props.order?.timeframes_by_return_type_data) {
    return true;
  } else {
    return (
      props.order.timeframes_by_return_type_data?.refund_allowed ||
      props.order.timeframes_by_return_type_data?.repair_allowed
    );
  }
});

const refundRepairTooltip = computed(() => {
  return {
    [returnTypesEnum.refund]: props.order?.timeframes_by_return_type_data
      ?.refund_allowed
      ? ""
      : t("daysNotice", {
          numberOfDays: t("numberOfDays", {
            count: props.order?.timeframes_by_return_type_data?.refund_days,
          }),
        }),
    [returnTypesEnum.repair]: t("daysNotice", {
      numberOfDays: props.order?.timeframes_by_return_type_data?.repair_allowed
        ? ""
        : t("numberOfDays", {
            count: props.order?.timeframes_by_return_type_data?.repair_days,
          }),
    }),
  };
});

const returnTypes = computed(() => {
  const returnTypeConfig = {
    [returnTypesEnum.refund]: {
      disabled: !props.order?.timeframes_by_return_type_data?.refund_allowed,
      tooltip: refundRepairTooltip.value[returnTypesEnum.refund],
    },
    [returnTypesEnum.repair]: {
      disabled: !props.order?.timeframes_by_return_type_data?.repair_allowed,
      tooltip: refundRepairTooltip.value[returnTypesEnum.repair],
    },
    [returnTypesEnum.service]: {
      disabled: false,
      tooltip: "",
    },
  };

  // Filter types based on isService flag
  const filteredTypes = returnTypesWithConditions.value.filter((type) => {
    if (isService.value) {
      // If isService is true, show only Service type
      return type.value === returnTypesEnum.service;
    } else {
      // If isService is false, show only Refund and Repair types (no Service)
      return type.value !== returnTypesEnum.service;
    }
  });

  return filteredTypes.map((type) => ({
    ...type,
    ...returnTypeConfig[type.value],
  }));
});

const returnAvailable = computed(() => {
  return (
    orderProductsData.value.some(
      (el) => el.quantityAvailableToReturn && el.can_be_returned_by_timeframe,
    ) &&
    props.order?.can_be_returned_by_amount &&
    props.order?.can_be_returned_by_order_status &&
    refundOrRepairAvailable.value
  );
});

// Check if there are regular (non-service) products available for return
const hasRegularProductsForReturn = computed(() => {
  return orderProductsData.value.some(
    (el) => 
      !el.is_service_product && 
      el.quantityAvailableToReturn && 
      el.can_be_returned_by_timeframe
  );
});

// Check if return buttons should be shown
const showReturnButtons = computed(() => {
  return !isReturn.value && props.order.po_number && hasRegularProductsForReturn.value && returnAvailable.value;
});

// Get reason_id value for radio buttons
function getReasonIdValue(index: number) {
  return returnForm.value.products[index]?.reason_id?.value || null;
}

// Get error message for reason_id field
function getReasonIdError(index: number): string {
  return errors.value[`products[${index}].reason_id`] || "";
}

// Set reason_id from radio buttons
function setReasonIdValue(index: number, value: string | number) {
  const numericValue = typeof value === "string" ? parseInt(value, 10) : value;
  const availableReasons = getAllowedReturnReasonsByReturnType(
    returnForm.value.products[index]?.type?.value,
  );
  const selectedReason = availableReasons.find((r) => r.value === numericValue);

  if (selectedReason) {
    returnForm.value.products[index].reason_id = selectedReason;
    setFieldValue(`products[${index}].reason_id`, selectedReason);
  }
}

function toggleDetails() {
  isExpanded.value = !isExpanded.value;
}

function isCanceledStatus(item: OrderStatus) {
  return item.order_status === orderStatusesOptions.CANCELED;
}

function successCallback(orderData: OrderData) {
  emit("updateOrderData", orderData);
}

function errorCallback(error) {
  console.log("error - ", error);
  openSystemDialog({
    type: "alert",
    title: `${t("error")}!`,
    text: error?.data?.errors?.[0]?.message || error?.data.errors,
  });
}

function scrollToCard() {
  const element = orderCardRef.value;
  if (!element) return;

  const headerOffset = 100;
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.scrollY - headerOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior: "auto",
  });
}

type ReturnTypeValue = (typeof returnForm.value.products)[number]["type"];

// function resetReasonField(
//   value: UiSelectModelValue<ReturnTypeValue>,
//   index: number,
// ) {
//   if (Array.isArray(value)) return;
//   const item = returnForm.value.products[index];
//   if (item) {
//     if (!isEqual(item.type, value)) {
//       item.reason_id = null;
//       setFieldValue(`products[${index}].reason_id`, null, false);
//     }
//     item.type = value || null;
//   }
// }

async function handleTypeChange(
  value: UiSelectModelValue<ReturnTypeValue>,
  index: number,
) {
  if (Array.isArray(value)) return;

  const item = returnForm.value.products[index];
  if (!item) return;

  const hasTypeChanged = !isEqual(item.type, value);

  // Update type first
  item.type = value || null;

  // If type changed, reset and set the first available reason after DOM update
  if (hasTypeChanged) {
    // Reset reason immediately
    item.reason_id = null;
    setFieldValue(`products[${index}].reason_id`, null, false);

    // Wait for DOM update, then set first available reason
    await nextTick();

    const availableReasons = getAllowedReturnReasonsByReturnType(value?.value);
    if (availableReasons.length > 0) {
      const firstReason = availableReasons[0];
      item.reason_id = firstReason;
      setFieldValue(`products[${index}].reason_id`, firstReason);
    }
  }
}
</script>
